/**
 * ListingDupesModal — Jul-2026 (User-Wunsch).
 *
 * FIN-Doubletten im Bestand: gruppiert alle Fahrzeuge der eigenen Organisation
 * mit identischer Fahrgestellnummer. Pro Gruppe wählt der Nutzer das Fahrzeug,
 * das behalten wird — die übrigen werden zusammengeführt (fehlende Felder
 * ergänzt) und gelöscht. Verkaufte/reservierte Fahrzeuge sind geschützt.
 */
import { useEffect, useState } from "react";
import { Loader2, Copy, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const _fmtD = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return ""; } };

const DupeGroup = ({ group, onDone }) => {
    const deletable = group.entries.filter((e) => !e.protected);
    const [keepId, setKeepId] = useState(deletable[0]?.id || group.entries[0].id);
    const [busy, setBusy] = useState(false);
    const removeIds = group.entries.filter((e) => !e.protected && e.id !== keepId).map((e) => e.id);
    const merge = async () => {
        if (!window.confirm(`${removeIds.length} Doublette(n) der FIN ${group.vin} löschen und in das gewählte Fahrzeug zusammenführen?`)) return;
        setBusy(true);
        try {
            const r = await api.post("/dealer/listing-duplicates/merge", { keep_id: keepId, remove_ids: removeIds });
            toast.success(`${r.data.removed} Doublette(n) zusammengeführt`);
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zusammenführen fehlgeschlagen");
        } finally { setBusy(false); }
    };
    return (
        <div className="border border-gray-200 rounded-sm p-3" data-testid={`dupe-group-${group.vin}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-black font-mono-num">FIN {group.vin} <span className="text-red-600">×{group.count}</span></div>
                {removeIds.length > 0 && (
                    <button type="button" onClick={merge} disabled={busy}
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-sm bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-60"
                        data-testid={`dupe-merge-${group.vin}`}>
                        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        {removeIds.length} löschen & zusammenführen
                    </button>
                )}
            </div>
            <div className="space-y-1">
                {group.entries.map((e) => (
                    <label key={e.id} className={`flex items-center gap-2 text-[11px] px-2 py-1.5 rounded-sm border cursor-pointer ${e.id === keepId ? "border-emerald-400 bg-emerald-50" : "border-gray-100 bg-gray-50"}`}
                        data-testid={`dupe-entry-${e.id}`}>
                        <input type="radio" name={`keep-${group.vin}`} checked={e.id === keepId}
                            disabled={e.protected} onChange={() => setKeepId(e.id)} />
                        <span className="font-bold truncate max-w-[220px]">{e.title || `${e.brand || ""} ${e.model || ""}`}</span>
                        {e.internal_number && <span className="font-mono-num text-gray-500">{e.internal_number}</span>}
                        {e.owner_label && <span className="text-[9px] font-bold uppercase bg-gray-100 border border-gray-300 rounded-sm px-1">{e.owner_label}</span>}
                        <span className="text-gray-500">{e.mileage != null ? `${Number(e.mileage).toLocaleString("de-DE")} km` : ""}</span>
                        <span className="text-gray-400">{e.created_at ? `angelegt ${_fmtD(e.created_at)}` : ""}</span>
                        {e.protected && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-amber-700"><ShieldCheck className="w-3 h-3" />verkauft/reserviert</span>}
                        {e.id === keepId && <span className="ml-auto text-[9px] font-black uppercase text-emerald-700">wird behalten</span>}
                    </label>
                ))}
            </div>
        </div>
    );
};

export default function ListingDupesModal({ open, onOpenChange, onDone }) {
    const [data, setData] = useState(null);
    const load = async () => {
        setData(null);
        try {
            const r = await api.get("/dealer/listing-duplicates");
            setData(r.data);
        } catch { setData({ groups: [], total_groups: 0 }); }
    };
    useEffect(() => { if (open) load(); }, [open]);
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="listing-dupes-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-base">
                        <Copy className="w-4 h-4 text-red-600" /> FIN-Doubletten im Bestand
                    </DialogTitle>
                </DialogHeader>
                <p className="text-[11px] text-gray-500 -mt-1">
                    Fahrzeuge deiner Organisation mit identischer Fahrgestellnummer. Wähle je Gruppe das Fahrzeug,
                    das behalten wird — fehlende Angaben werden aus den Doubletten übernommen, der Rest wird gelöscht.
                </p>
                {data === null ? (
                    <div className="py-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /> Suche Doubletten…</div>
                ) : data.groups.length === 0 ? (
                    <div className="py-8 text-center text-sm text-emerald-700 font-bold" data-testid="dupes-empty">
                        ✓ Keine FIN-Doubletten im Bestand gefunden.
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="text-xs font-bold text-red-700" data-testid="dupes-count">
                            {data.total_groups} FIN{data.total_groups === 1 ? "" : "s"} mehrfach vergeben · {data.total_duplicates} überzählige Fahrzeuge
                        </div>
                        {data.groups.map((g) => <DupeGroup key={g.vin} group={g} onDone={() => { load(); onDone?.(); }} />)}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
